if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();

      if (this.hasChildNodes()) {
        this.init();
      }
    }

    init() {
      this.setAttribute('data-3dmark-subtotal-initialized', 'true');

      this.form = this.querySelector('.js-product-form');
      this.section = this.closest('.js-product') || document;

      if (!this.form) return;

      this.submitBtn = this.querySelector('[name="add"]');
      this._priceCache = new Map();

      if (theme.settings.afterAtc !== 'no-js') {
        this.form.addEventListener(
          'submit',
          (event) => {
            event.stopImmediatePropagation();
            this.handleSubmit(event);
          },
          true
        );
      }

      this.form.addEventListener('change', () => {
        this.updateSubtotal();
      });

      if (this.section) {
        this.section.addEventListener('change', (event) => {
          if (
            event.target &&
            event.target.matches &&
            event.target.matches('[data-addon-checkbox]')
          ) {
            this.updateSubtotal();
          }
        });

        this.section.addEventListener('on:variant:change', () => {
          this.updateSubtotal();
        });
      }

      const idInput = this.form.querySelector('input[name="id"]');

      if (idInput) {
        new MutationObserver(() => {
          this.updateSubtotal();
        }).observe(idInput, {
          attributes: true,
          attributeFilter: ['value']
        });
      }

      this.updateSubtotal();
    }

    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.setErrorMsgState();

      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      const mainQuantity = parseInt(formData.get('quantity'), 10) || 1;
      const cartDrawer = document.querySelector('cart-drawer');
      const quickAddDrawer = this.closest('quick-add-drawer');
      const shouldRedirectToCart = theme.settings.afterAtc === 'page';

      const addonChecks = this.section?.querySelectorAll('[data-addon-checkbox]:checked') || [];
      const hasSelectedAddons = addonChecks.length > 0;

      /**
       * 3DMARK：
       * 每一次主商品 + 加購品加入，都產生一組獨立 bundleId。
       * 這組 bundleId 會同時寫到主商品與加購品。
       */
      const bundleId = hasSelectedAddons
        ? `3dmark-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        : '';

      if (bundleId) {
        formData.append('properties[_3dmark_bundle_id]', bundleId);
      }

      let sections = 'cart-icon-bubble';

      if (cartDrawer) {
        const cartDrawerSection = cartDrawer.closest('.shopify-section');

        if (cartDrawerSection) {
          sections += `,${cartDrawerSection.id.replace('shopify-section-', '')}`;
        }
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const data = await response.json();

        if (data.status) {
          const error =
            data.errors && typeof data.errors === 'object'
              ? Object.values(data.errors).flat()
              : data.description || data.message;

          this.setErrorMsgState(error);
        }

        if (!response.ok) {
          throw new Error(response.status);
        }

        if (!shouldRedirectToCart) {
          ProductForm.updateCartIcon(data);

          if (quickAddDrawer) {
            quickAddDrawer.addedToCart();
          }

          if (cartDrawer) {
            cartDrawer.renderContents(data, !quickAddDrawer && theme.settings.afterAtc === 'drawer');
          } else if (window.location.pathname === theme.routes.cart) {
            const cartItems = document.querySelector('cart-items');

            if (cartItems) {
              if (cartItems.dataset.empty === 'true') {
                window.location.reload();
              } else {
                cartItems.refresh();
              }
            }
          }
        }

        /**
         * 3DMARK：
         * 加購品加入前，先暫停 guard，避免剛加入時被誤判。
         */
        window.threeDMarkAddonCartGuardPausedUntil = Date.now() + 10000;

        const addonResult = await this.addSelectedAddons({
          addonChecks,
          sections,
          parentData: data,
          parentQuantity: mainQuantity,
          parentBundleId: bundleId,
          shouldRedirectToCart
        });

        if (!shouldRedirectToCart && addonResult) {
          ProductForm.updateCartIcon(addonResult);

          const refreshedDrawer = document.querySelector('cart-drawer');

          if (refreshedDrawer && typeof refreshedDrawer.renderContents === 'function') {
            refreshedDrawer.renderContents(addonResult, false);
          } else if (window.location.pathname === theme.routes.cart) {
            const cartItems = document.querySelector('cart-items');

            if (cartItems && typeof cartItems.refresh === 'function') {
              cartItems.refresh();
            } else {
              window.location.reload();
            }
          }
        }

        if (shouldRedirectToCart) {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);

          return;
        }

        this.updateSubtotal();

        this.dispatchEvent(
          new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: data.variant_id
            }
          })
        );
      } catch (error) {
        console.log(error); // eslint-disable-line

        this.dispatchEvent(
          new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: {
              error: this.errorMsg?.textContent
            }
          })
        );

        document.querySelector('cart-drawer')?.refresh(true);
      } finally {
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');

        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading', 'is-success');
        }, 1400);
      }
    }

    async addSelectedAddons({
      addonChecks,
      sections,
      parentData,
      parentQuantity,
      parentBundleId,
      shouldRedirectToCart
    }) {
      if (!addonChecks || addonChecks.length === 0) return null;

      const addonItems = [];
      const productTitleEl = this.closest('.product')?.querySelector('.product-title');
      const parentTitle = productTitleEl?.textContent.trim() || 'Main product';

      const parentLineKey = String(parentData.key || '');
      const parentVariantId = String(parentData.variant_id || this.form.querySelector('input[name="id"]')?.value || '');
      const mainBundleId = String(parentBundleId || parentLineKey || parentVariantId || '');

      addonChecks.forEach((checkbox) => {
        const addonVariantId = checkbox?.dataset.variantId;

        if (!this.isValidVariantId(addonVariantId)) return;

        const parentProductId = checkbox.dataset.parentProductId || '';
        const checkboxParentVariantId = checkbox.dataset.parentVariantId || '';
        const finalParentVariantId = String(parentVariantId || checkboxParentVariantId || '');

        const maxAddonQuantity = parseInt(parentQuantity, 10) || 1;
        const requestedAddonQuantity = parseInt(checkbox.dataset.addonQuantity || '1', 10) || 1;
        const addonQuantity = Math.min(requestedAddonQuantity, maxAddonQuantity);

        /**
         * 3DMARK：
         * addonGroup 用 bundleId。
         * addonUniqueKey 每筆加購品都不同，用來避免 Shopify 合併同 variant 加購商品。
         */
        const addonGroup = `${mainBundleId}-${addonVariantId}`;
        const addonUniqueKey = `${addonGroup}-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 8)}`;

        addonItems.push({
          id: parseInt(addonVariantId, 10),
          quantity: addonQuantity,
          properties: {
            '_addon_only': 'true',
            '_parent_bundle_id': mainBundleId,
            '_parent_line_key': parentLineKey,
            '_parent_product_id': String(parentProductId),
            '_parent_variant_id': String(finalParentVariantId),
            '_addon_variant_id': String(addonVariantId),
            '_addon_group': addonGroup,
            '_addon_for': parentTitle,
          }
        });
      });

      if (!addonItems.length) return null;

      try {
        const addonResponse = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: addonItems,
            sections,
            sections_url: window.location.pathname
          })
        });

        const addonData = await addonResponse.json();

        if (!addonResponse.ok) {
          throw new Error(addonData.message || addonData.description || addonResponse.status);
        }

        return addonData;
      } catch (error) {
        console.error('[3DMARK] Add addon failed:', error);
        return null;
      }
    }

    static updateCartIcon(response) {
      const el = document.getElementById('cart-icon-bubble');

      if (el && response && response.sections && response.sections['cart-icon-bubble']) {
        el.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    setErrorMsgState(error) {
      const el = this.errorMsg || (this.errorMsg = this.querySelector('.js-form-error'));

      if (!el) return;

      el.hidden = !error;

      if (error) {
        el.innerHTML = '';

        [].concat(error).forEach((err, index) => {
          if (index) {
            el.insertAdjacentHTML('beforeend', '<br>');
          }

          el.insertAdjacentText('beforeend', err);
        });
      }
    }

    isValidVariantId(variantId) {
      if (variantId == null) return false;

      const str = String(variantId).trim();

      if (!/^\d+$/.test(str)) return false;

      const n = parseInt(str, 10);

      return Number.isFinite(n) && n > 0;
    }

    getAddonQuantityForCheckbox(checkbox, maxQuantity) {
      const maxAddonQuantity = parseInt(maxQuantity, 10) || 1;
      const requestedAddonQuantity = parseInt(checkbox.dataset.addonQuantity || '1', 10) || 1;

      return Math.min(requestedAddonQuantity, maxAddonQuantity);
    }

    formatMoney(cents) {
      try {
        if (window.Shopify?.formatMoney) {
          return Shopify.formatMoney(
            Math.round(cents),
            theme.moneyFormat || theme.settings?.moneyFormat
          );
        }
      } catch (error) {}

      const currency = Shopify?.currency?.active || 'USD';

      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency
      }).format((cents || 0) / 100);
    }

    async getVariantPriceCents(variantId) {
      if (!this.isValidVariantId(variantId)) return 0;

      if (this._priceCache.has(variantId)) {
        return this._priceCache.get(variantId);
      }

      try {
        const res = await fetch(`/variants/${variantId}.json`);

        if (!res.ok) {
          throw new Error(res.status);
        }

        const json = await res.json();
        const priceStr = json?.product_variant?.price || json?.product_variant?.price_amount;
        const cents = Math.round(parseFloat(priceStr) * 100);

        this._priceCache.set(variantId, cents);

        return cents;
      } catch (error) {
        return 0;
      }
    }

    async updateSubtotal() {
      const target = (this.section || this).querySelector('[data-3dmark-subtotal]');

      if (!target) return;

      const qty = parseInt(this.form.querySelector('[name="quantity"]')?.value || '1', 10) || 1;
      const mainVariantId = this.form.querySelector('input[name="id"]')?.value || '';

      let mainPrice = this.isValidVariantId(mainVariantId)
        ? await this.getVariantPriceCents(mainVariantId)
        : 0;

      let addonsTotal = 0;

      for (const checkbox of (this.section || this).querySelectorAll('[data-addon-checkbox]:checked')) {
        const addonId = checkbox?.dataset.variantId;

        if (this.isValidVariantId(addonId)) {
          const addonPrice = await this.getVariantPriceCents(addonId);
          const addonQuantity = this.getAddonQuantityForCheckbox(checkbox, qty);

          addonsTotal += addonPrice * addonQuantity;
        }
      }

      target.textContent = this.formatMoney(mainPrice * qty + addonsTotal);
    }
  }

  customElements.define('product-form', ProductForm);
}